import { useEffect, useRef, useState } from 'react';

import { faGrid2 } from '@fortawesome/pro-regular-svg-icons';

import { useConfigContext } from 'lib/core/config';

import { useAgentContext } from 'lib/common/contexts/AgentContext';
import { useContactContext } from 'lib/common/contexts/ContactContext';
import { useSoftphoneModalContext } from 'lib/common/contexts/SoftphoneModalContext';

import ActionSheet from 'lib/common/hooks/useIQTextSelection/components/IQTextSelectionPopper/components/ActionSheet';

import Button from 'lib/common/components/Button';
import { LogEvents, logger } from 'lib/common/components/LoggerController';
import Text from 'lib/common/components/Text';
import WarningBanner from 'lib/common/components/WarningBanner';
import InfoPanel from 'lib/common/components/atoms/InfoPanel';

import { DESKTOP_SHORTCUTS_NAV_OPTION } from 'lib/common/constants/softphoneDefaultNavOptions';

import SoftphoneNavMenuFolder from '../SoftphoneSidebar/components/SoftphoneNavMenuFolder';

import styles from './acgr-warning.module.scss';

interface Subscription {
  // AWS types use Function
  // eslint-disable-next-line @typescript-eslint/ban-types
  unsubscribe: Function;
}

export default function ACGRWarning({ className }: { className?: string }) {
  const { config } = useConfigContext();
  const { agent } = useAgentContext();
  const {
    actions: { forceClearAllTasks },
  } = useContactContext();
  const {
    actions: { openModal, closeModal },
  } = useSoftphoneModalContext();

  const acgrInitializedRef = useRef(false);
  const failoverPendingSubscriptionRef = useRef<Subscription | null>(null);

  const [isInactiveRegion, setIsInactiveRegion] = useState(false);
  const [isFailoverPending, setIsFailoverPending] = useState(false);

  useEffect(() => {
    if (!agent || !config?.ACGR || acgrInitializedRef.current) {
      return;
    }

    acgrInitializedRef.current = true;

    const acgrConfigurationErrorSubscription = connect.globalResiliency.onConfigureError((error) => {
      logger.error(LogEvents.ACGR_CONFIGURATION_ERROR, { error });
    });

    failoverPendingSubscriptionRef.current = connect.globalResiliency.onFailoverPending(() => {
      setIsFailoverPending(true);
    });

    const failoverCompletedSubscription = connect.globalResiliency.onFailoverCompleted(({ activeRegion }) => {
      failoverPendingSubscriptionRef.current?.unsubscribe();

      failoverPendingSubscriptionRef.current = connect.globalResiliency.onFailoverPending(({ nextActiveRegion }) => {
        setIsFailoverPending(activeRegion !== nextActiveRegion);
      });

      setIsFailoverPending(false);
      setIsInactiveRegion(connect.globalResiliency.getActiveRegion() !== config.CONNECT_REGION);

      // Clear tasks on failover to avoid issues with tasks that are stuck in an inconsistent state due to the failover
      forceClearAllTasks();
    });

    setIsInactiveRegion(connect.globalResiliency.getActiveRegion() !== config.CONNECT_REGION);

    return () => {
      acgrConfigurationErrorSubscription.unsubscribe();
      failoverCompletedSubscription.unsubscribe();
      failoverPendingSubscriptionRef.current?.unsubscribe();
      acgrInitializedRef.current = false;
    };
  }, [agent, config]);

  useEffect(() => {
    // If we display the ACGR Action Sheet, we don't want the E911 Modal to overlay
    // above it. Hence, we're closing the only open modal, which is the E911 modal
    // If there are no modals open, closeModal will do nothing
    if (isInactiveRegion && !isFailoverPending) {
      closeModal();
    }
  }, [isInactiveRegion, isFailoverPending]);

  if (!isInactiveRegion && !isFailoverPending) {
    return null;
  }

  if (isFailoverPending && !isInactiveRegion) {
    const forceFailover = () => {
      openModal({
        title: 'Confirm',
        onSave: () => {
          // @ts-expect-error AWS Geniuses forgot to add this to types
          connect.globalResiliency.forceFailover();
          forceClearAllTasks();
          closeModal();
        },
        primaryButtonText: 'Continue',
        secondaryButtonText: 'Cancel',
        content: (
          <Text>
            We’ll force close all current interactions, then you’ll be required to continue in the backup environment.
            <br />
            <br /> Continue only if you have completed in-progress contacts or cannot finish them manually. This action
            cannot be undone.
          </Text>
        ),
      });
    };

    return (
      <WarningBanner
        className={className}
        title={
          <Text className={styles['acgr-warning__title']} medium>
            Backup Switch Scheduled
          </Text>
        }
        body={
          <Text className={styles['acgr-warning__body']}>
            Your Organisation is moving to a backup environment. Please wrap up your current work. If you run into
            trouble, click{' '}
            <Text className={styles['acgr-warning__link']} onClick={forceFailover} data-testid="open-failover-modal">
              here
            </Text>{' '}
            to force close your interactions.
          </Text>
        }
      />
    );
  }

  const switchToBackupEnvironment = () => {
    if (!config?.ACGR?.linkedCoreAccount?.regionCode) {
      return;
    }

    window.location.href = window.location.origin.replace(
      config.DEPLOY_REGION_CODE,
      config.ACGR.linkedCoreAccount.regionCode
    );
  };

  const desktopShortcutsOptions = DESKTOP_SHORTCUTS_NAV_OPTION(config);

  return (
    <ActionSheet isOpen={isInactiveRegion} onClose={() => {}} className={styles['acgr-warning__action-sheet']}>
      <div className="next-to-10 flex-column align-start">
        <Text type="heading3"> Switch to Backup Environment</Text>
        <div className={styles['acgr-warning__separator']} />
        <InfoPanel type="WARNING">
          <Text>
            Your organisation has moved to a backup environment. Click{' '}
            <Text medium inline>
              Switch
            </Text>{' '}
            to continue.
          </Text>
        </InfoPanel>
        <Button fullWidth className="mt-5" onClick={switchToBackupEnvironment}>
          Switch
        </Button>
        <SoftphoneNavMenuFolder
          offset={10}
          offsetLeft={0}
          anchor={
            <Button icon={faGrid2} fullWidth styleType="SECONDARY" onClick={switchToBackupEnvironment}>
              {desktopShortcutsOptions.name}
            </Button>
          }
          {...desktopShortcutsOptions}
        />
      </div>
    </ActionSheet>
  );
}
